import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { applyDocumentLocale, i18next, readStoredLocale, storeLocale, type Locale } from '@/i18n';

interface LocaleContextValue {
  locale: Locale;
  dir: 'rtl' | 'ltr';
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());

  useEffect(() => {
    // The inline script in index.html already set these before first paint;
    // this keeps them true after a change.
    applyDocumentLocale(locale);
    if (i18next.language !== locale) void i18next.changeLanguage(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    storeLocale(next);
    setLocaleState(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, dir: locale === 'ar' ? 'rtl' : 'ltr', setLocale }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale used outside LocaleProvider');
  return value;
}
